<script lang="ts" setup>
import { ref, computed, onMounted, nextTick, watch, useSlots } from 'vue';
import { useModalStore } from '@/modules/core/common/store/modal';
import { normalizeClasses } from '@/helpers/Utils';

defineOptions({
    name: 'ideo-modal',
    inheritAttrs: false
});

const props = defineProps({
  "modelValue": { type: Boolean,  },
  "title": { default: null },
  "static": { type: Boolean,  },
  "centered": { type: Boolean,  },
  "scrollable": { type: Boolean,  },
  "fullscreen": { type: Boolean,  },
  "size": { default: 'md' },
  "hideHeader": { type: Boolean,  },
  "hideFooter": { type: Boolean,  },
  "hideClose": { type: Boolean,  },
  "modalClass": { default: () => ({}) },
  "backdropClass": { default: () => ({}) },
  "contentClass": { default: () => ({}) },
  "headerClass": { default: () => ({}) },
  "bodyClass": { default: () => ({}) },
  "footerClass": { default: () => ({}) }
});

const emit = defineEmits(["ok", "close", "hidden", "shown", "update:modelValue"]);

const slots = useSlots();
const modalStore = useModalStore();

const isMounted = ref(false);
const isVisible = ref(false);
const modalZIndex = ref(10313);
const showConfirmation = ref(false);

const backdropClasses = computed(() => ({
    'd-none': !isVisible.value,
    ...normalizeClasses(props.backdropClass)
}));

const modalClasses = computed(() => ({
    'd-block': isVisible.value,
    'scroll scroll--modal': true,
    ...normalizeClasses(props.modalClass)
}));

const flag = (value: any): boolean =>
{
    return value !== false;
};

const dialogClasses = computed(() => ({
    'modal-dialog-centered': flag(props.centered),
    'modal-dialog-scrollable': flag(props.scrollable),
    'modal-fullscreen': flag(props.fullscreen),
    [`modal-${props.size}`]: props.size != 'md'
}));

const contentClasses = computed(() => normalizeClasses(props.contentClass));
const headerClasses = computed(() => normalizeClasses(props.headerClass));
const bodyClasses = computed(() => normalizeClasses(props.bodyClass));
const footerClasses = computed(() => normalizeClasses(props.footerClass));

const hasHelperSlot = computed(() => !!slots.helper);

onMounted(() =>
{
    isMounted.value = true;
});

const updateModel = (): void =>
{
    emit('update:modelValue', isVisible.value);
};

const hide = (): void =>
{
    modalStore.decrementOpenedModalCounter();

    modalZIndex.value = 10313;
    isVisible.value = false;
    updateModel();

    nextTick(() =>
    {
        emit('hidden');

        if (!flag(props.static))
        {
            detachListeners();
        }
    });
};

const show = (): void =>
{
    const counter = modalStore.incrementOpenedModalCounter();

    modalZIndex.value = modalZIndex.value + counter;
    isVisible.value = true;
    updateModel();

    nextTick(() =>
    {
        emit('shown');

        if (!flag(props.static))
        {
            attachListeners();
            startInitialCapture();
        }
    });
};

const onOk = (): void =>
{
    hide();
    emit('ok');
};

const onClose = (): void =>
{
    hide();
    emit('close');
};

watch(() => props.modelValue, (value: boolean, old: boolean) =>
{
    if (value != old)
        isVisible.value = value;
}, { immediate: true });

const toggle = (): void =>
{
    if (isVisible.value)
        hide();
    else
        show();
};

const onOutOfFocus = (event: MouseEvent): void =>
{
    if (!flag(props.static))
    {
        checkDirty();

        if (initialCaptured.value && isDirty.value)
        {
            showConfirmation.value = true;
        }
        else
        {
            hide();
        }
    }
    else
    {
        hide();
    }
};

const onConfirmClose = (): void =>
{
    showConfirmation.value = false;
    hide();
};

const onCancelClose = (): void =>
{
    showConfirmation.value = false;
};

const visibilityState = (): boolean =>
{
    return isVisible.value;
};

defineExpose({
    toggle,
    show,
    hide,
    visibilityState
});

// watch for changes
const initialState = ref<Record<string, any>>({});
const currentState = ref<Record<string, any>>({});
const isDirty = ref(false);
const initialCaptured = ref(false);

let inputListener: ((e: Event) => void) | null = null;
let initialCaptureInterval: number | null = null;
let initialCaptureStartAt = 0;
let lastSnapshot = '';

const STABLE_MS = 500;
const MAX_WAIT_MS = 5000;

function captureState()
{
    const values: Record<string, any> = {};

    const modalBody = document.querySelector('.modal-content .modal-body');

    if (!modalBody)
    {
        return values;
    }

    modalBody.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea')
        .forEach((el, index) =>
        {
            const key = `value-${index}`;

            if (el.type === 'checkbox' || el.type === 'radio')
            {
                values[key] = (el as HTMLInputElement).checked ? '1' : '0';
            }
            else
            {
                values[key] = el.value;
            }
        });

    return values;
}

function checkDirty()
{
    currentState.value = captureState();

    if (!initialCaptured.value)
    {
        isDirty.value = false;

        return;
    }

    isDirty.value = JSON.stringify(initialState.value) !== JSON.stringify(currentState.value);

}

function startInitialCapture()
{
    stopInitialCapture();

    initialCaptured.value = false;
    initialState.value = {};
    lastSnapshot = '';
    initialCaptureStartAt = Date.now();

    initialCaptureInterval = window.setInterval(() =>
    {
        const snapshot = JSON.stringify(captureState());

        const now = Date.now();

        if (snapshot !== lastSnapshot)
        {
            lastSnapshot = snapshot;
            // reset stability timer to now
            initialCaptureStartAt = now;
        }

        const stableFor = now - initialCaptureStartAt;

        if (stableFor >= STABLE_MS || now - (initialCaptureStartAt - 0) >= MAX_WAIT_MS)
        {
            initialState.value = JSON.parse(snapshot || '{}');
            initialCaptured.value = true;
            stopInitialCapture();
        }
    }, 100);
}

function stopInitialCapture()
{
    if (initialCaptureInterval !== null)
    {
        clearInterval(initialCaptureInterval);
        initialCaptureInterval = null;
    }
}

function attachListeners()
{
    const modalBody = document.querySelector('.modal-content .modal-body');

    if (!modalBody)
    {
        return;
    }

    inputListener = () =>
    {
        checkDirty();
    };

    modalBody.addEventListener('input', inputListener, true);
    modalBody.addEventListener('change', inputListener, true);
}

function detachListeners()
{
    const modalBody = document.querySelector('.modal-content .modal-body');

    if (modalBody && inputListener)
    {
        modalBody.removeEventListener('input', inputListener, true);
        modalBody.removeEventListener('change', inputListener, true);
        inputListener = null;
    }

    stopInitialCapture();
}
</script>

<template>
    <teleport to="body" v-if="isMounted && isVisible">
        <div class="modal-backdrop" :class="backdropClasses"></div>
        <div class="modal" v-bind="$attrs" :class="modalClasses" @mousedown.self="onOutOfFocus">
            <div class="modal-dialog" :class="dialogClasses">
                <div class="modal-content" :class="contentClasses" @click.stop>
                    <div class="modal-header" :class="headerClasses" v-if="!flag(hideHeader)">
                        <slot name="modal-header">
                            <h5 class="modal-title">{{ title }}</h5>
                            <button type="button" class="close" @click.stop="onClose" v-if="!flag(hideClose)">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </slot>
                    </div>
                    <div class="modal-body" :class="bodyClasses">
                        <div class="row">
                            <div class="mb-3 mb-lg-0 d-flex flex-column flex-fill" :class="hasHelperSlot ? 'col-lg-7' : 'col-lg-12'">
                                <slot name="default"></slot>
                            </div>
                            <div v-if="hasHelperSlot" class="col-lg-5">
                                <slot name="helper"></slot>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer" :class="footerClasses" v-if="!flag(hideFooter)">
                        <slot name="modal-footer" :ok="() => onOk()" :cancel="() => onClose()">
                            <button type="button" class="btn btn-secondary" @click.stop="onClose">
                                <slot name="btn-close">
                                    {{ $t('[[[Zamknij]]]') }}
                                </slot>
                            </button>
                            <button type="button" class="btn btn-primary" @click.stop="onOk">
                                <slot name="btn-submit">
                                    {{ $t('[[[Ok]]]') }}
                                </slot>
                            </button>
                        </slot>
                    </div>
                </div>
            </div>
        </div>
        <!-- Confirmation Dialog for dirty form -->
        <div v-if="showConfirmation" class="confirmation-overlay" @click="onCancelClose">
            <div class="confirmation-dialog" @click.stop>
                <div class="confirmation-content">
                    <h5 class="confirmation-title">Potwierdzenie</h5>
                    <p class="confirmation-message">Czy na pewno chcesz zamknąć okno? Wprowadzone dane formularza zostaną utracone.</p>
                    <div class="confirmation-actions">
                        <button type="button" class="btn btn-secondary me-2" @click="onCancelClose">
                            Nie
                        </button>
                        <button type="button" class="btn btn-danger" @click="onConfirmClose">
                            Tak
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<style lang="scss">
.modal-backdrop {
    opacity: 0.5;
    z-index: calc(v-bind(modalZIndex) - 1) !important;
}

.modal {
    overflow-x: hidden !important;
    overflow-y: auto !important;
    z-index: v-bind(modalZIndex) !important;

    button.close {
        background-color: transparent;
        border: 0;
        font-size: 1.21875rem;
        font-weight: 700;
        line-height: 1;
    }
}

.modal-dialog-sm {
    max-width: 300px !important;
}

.modal-dialog-lg {
    max-width: 800px !important;
}

.modal-dialog-xl {
    max-width: 1140px !important;
}

.modal-xxl {
    max-width: 1600px !important;
}

.confirmation-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: calc(v-bind(modalZIndex) + 1000) !important;
}

.confirmation-dialog {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    max-width: 400px;
    width: 90%;
    margin: 20px;
}

.confirmation-content {
    padding: 24px;
}

.confirmation-title {
    margin-bottom: 16px;
    font-weight: 600;
    color: #333;
}

.confirmation-message {
    margin-bottom: 24px;
    color: #666;
    line-height: 1.5;
}

.confirmation-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
